import { useState, useCallback, useMemo } from "react";

export interface SingleTablePaginationState {
  page: number;
  pageSize: number;
  total: number;
}

export interface SingleTablePaginationActions {
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setTotal: (total: number) => void;
  resetPagination: () => void;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
}

export interface UseSingleTablePaginationProps {
  initialPage?: number;
  initialPageSize?: number;
  initialTotal?: number;
}

export interface UseSingleTablePaginationReturn
  extends SingleTablePaginationState,
    SingleTablePaginationActions {
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  startIndex: number;
  endIndex: number;
  isFirstPage: boolean;
  isLastPage: boolean;
  paginationProps: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
}

const useSingleTablePagination = ({
  initialPage = 1,
  initialPageSize = 5,
  initialTotal = 0,
}: UseSingleTablePaginationProps = {}): UseSingleTablePaginationReturn => {
  const [page, setPage] = useState<number>(initialPage);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [total, setTotal] = useState<number>(initialTotal);

  // Computed values
  const totalPages = useMemo(
    () => Math.ceil(total / pageSize),
    [total, pageSize],
  );
  const hasNextPage = useMemo(() => page < totalPages, [page, totalPages]);
  const hasPrevPage = useMemo(() => page > 1, [page]);
  const startIndex = useMemo(() => (page - 1) * pageSize + 1, [page, pageSize]);
  const endIndex = useMemo(
    () => Math.min(page * pageSize, total),
    [page, pageSize, total],
  );
  const isFirstPage = useMemo(() => page === 1, [page]);
  const isLastPage = useMemo(() => page === totalPages, [page, totalPages]);

  // Actions
  const handleSetPage = useCallback(
    (newPage: number) => {
      if (newPage >= 1 && newPage <= totalPages) {
        setPage(newPage);
      }
    },
    [totalPages],
  );

  const handleSetPageSize = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  }, []);

  const handleSetTotal = useCallback((newTotal: number) => {
    setTotal(newTotal);
  }, []);

  const resetPagination = useCallback(() => {
    setPage(initialPage);
    setPageSize(initialPageSize);
    setTotal(initialTotal);
  }, [initialPage, initialPageSize, initialTotal]);

  const goToPage = useCallback(
    (targetPage: number) => {
      handleSetPage(targetPage);
    },
    [handleSetPage],
  );

  const nextPage = useCallback(() => {
    if (hasNextPage) {
      handleSetPage(page + 1);
    }
  }, [hasNextPage, page, handleSetPage]);

  const prevPage = useCallback(() => {
    if (hasPrevPage) {
      handleSetPage(page - 1);
    }
  }, [hasPrevPage, page, handleSetPage]);

  // Props for TablePagination component
  const paginationProps = useMemo(
    () => ({
      page,
      pageSize,
      total,
      onPageChange: handleSetPage,
      onPageSizeChange: handleSetPageSize,
    }),
    [page, pageSize, total, handleSetPage, handleSetPageSize],
  );

  return {
    // State
    page,
    pageSize,
    total,

    // Computed
    totalPages,
    hasNextPage,
    hasPrevPage,
    startIndex,
    endIndex,
    isFirstPage,
    isLastPage,

    // Actions
    setPage: handleSetPage,
    setPageSize: handleSetPageSize,
    setTotal: handleSetTotal,
    resetPagination,
    goToPage,
    nextPage,
    prevPage,

    // Props for components
    paginationProps,
  };
};

export default useSingleTablePagination;
